import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import EventIcon from '@mui/icons-material/Event';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShareIcon from '@mui/icons-material/Share';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LockIcon from '@mui/icons-material/Lock';
import ErrorIcon from '@mui/icons-material/Error';
import QuizIcon from '@mui/icons-material/Quiz';
import { getDashboardStats } from '../services/internApi';
import { formatCurrency } from '../utils/formatters';
import useAuth from '../hooks/useAuth';
import StripeConnectBanner from '../components/StripeConnectBanner';

function RegistrationProgress({ progress, onAction }) {
  if (!progress) return null;
  const { stages = [], current_stage_key, overall_status, test_status, test_attempt_count, test_next_eligible_at, test_in_progress, test_last_percentage } = progress;
  if (overall_status === 'approved') return null; // hide once approved

  const isPermanentlyIneligible = overall_status === 'permanently_ineligible';
  const isTestStage = current_stage_key === 'pending_assessment';
  const nowMs = Date.now();
  const lockedMs = test_next_eligible_at ? new Date(String(test_next_eligible_at).replace('Z','')).getTime() : null;
  const testLocked = lockedMs && lockedMs > nowMs;

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, background: 'linear-gradient(135deg, #f0f9ff, #ede9fe)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6">Your application progress</Typography>
      </Box>
      <Grid container spacing={1.5}>
        {stages.map((stage, idx) => {
          let icon, color;
          if (stage.state === 'done') { icon = <CheckCircleIcon />; color = '#16a34a'; }
          else if (stage.state === 'current') { icon = <RadioButtonUncheckedIcon />; color = '#7c3aed'; }
          else if (stage.state === 'failed') { icon = <ErrorIcon />; color = '#dc2626'; }
          else { icon = <LockIcon />; color = '#9ca3af'; }
          return (
            <Grid item xs={12} sm={6} md={4} key={stage.key}>
              <Paper variant="outlined" sx={{ p: 1.5, borderColor: color, opacity: stage.state === 'locked' ? 0.55 : 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Box sx={{ color }}>{icon}</Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {idx + 1}. {stage.label}
                  </Typography>
                  {stage.state === 'current' && <Chip label="Now" size="small" color="primary" sx={{ height: 18 }} />}
                </Box>
                <Typography variant="caption" color="text.secondary">{stage.description}</Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {isTestStage && !isPermanentlyIneligible && (
        <Box sx={{ mt: 2 }}>
          {test_in_progress ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <AlertTitle>You have an in-progress test</AlertTitle>
              Your test timer is still running. Resume now or it will auto-submit when the timer expires.
            </Alert>
          ) : testLocked ? (
            <Alert severity="info" sx={{ mb: 1 }}>
              <AlertTitle>Retake on hold</AlertTitle>
              You can retake the onboarding test starting {new Date(String(test_next_eligible_at).replace('Z','')).toLocaleString()}.
              Attempt {test_attempt_count} of 4 used so far.
            </Alert>
          ) : test_attempt_count > 0 && test_status === 'failed' ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <AlertTitle>Try again</AlertTitle>
              Your last attempt scored {test_last_percentage}%. You need 75% to pass.
              Attempt {test_attempt_count + 1} of 4.
            </Alert>
          ) : null}
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<QuizIcon />}
            onClick={onAction}
            sx={{ background: 'linear-gradient(135deg, #1e3a8a, #7c3aed)' }}
            disabled={testLocked}
          >
            {test_in_progress ? 'Resume Test' : test_attempt_count > 0 ? 'Take Retake' : 'Start Onboarding Test'}
          </Button>
        </Box>
      )}

      {isPermanentlyIneligible && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>Onboarding test — maximum attempts reached</AlertTitle>
          You have used all 4 attempts. Contact <a href="mailto:support@cyberwisecorp.com">support@cyberwisecorp.com</a> if you'd like to discuss other opportunities.
        </Alert>
      )}
    </Paper>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getDashboardStats()
      .then((data) => setStats(data))
      .catch((err) => setError(err?.response?.data?.error || err.message || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const cards = [
    {
      label: 'Available Jobs',
      value: stats?.available_jobs_count ?? 0,
      sublabel: 'Open for you to accept',
      icon: WorkOutlineIcon,
      color: '#7c3aed',
      action: () => navigate('/jobs'),
      actionLabel: 'View jobs',
    },
    {
      label: 'Upcoming Bookings',
      value: stats?.upcoming_bookings_count ?? 0,
      sublabel: 'Confirmed assessments',
      icon: EventIcon,
      color: '#2563eb',
      action: () => navigate('/bookings'),
      actionLabel: 'View bookings',
    },
    {
      label: 'Total Earned',
      value: formatCurrency(stats?.earnings?.total_earned ?? 0),
      sublabel: `${stats?.earnings?.completed_count ?? 0} assessments completed`,
      icon: AttachMoneyIcon,
      color: '#16a34a',
      action: () => navigate('/earnings'),
      actionLabel: 'View earnings',
    },
    {
      label: 'Referrals',
      value: stats?.referral_count ?? 0,
      sublabel: `${formatCurrency((stats?.referral_count ?? 0) * 10)} in bonuses`,
      icon: ShareIcon,
      color: '#d97706',
      action: () => navigate('/referrals'),
      actionLabel: 'My referrals',
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar
          src={stats?.photo_url || undefined}
          alt={stats?.name || 'Intern'}
          sx={{ width: 56, height: 56, bgcolor: 'primary.light', fontSize: 22 }}
        >
          {(stats?.name || '?').slice(0, 1).toUpperCase()}
        </Avatar>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Welcome back{stats?.name ? `, ${stats.name.split(' ')[0]}` : ''}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Here's what's happening with your CWC work.
          </Typography>
        </Box>
      </Box>

      {stats?.status === 'approved' && (
        <StripeConnectBanner
          payoutsEnabled={stats?.stripe_payouts_enabled}
          graceDaysRemaining={stats?.stripe_grace_days_remaining}
          requirementsDue={stats?.stripe_requirements_due}
        />
      )}

      {stats?.flagged_for_review && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Your account is flagged for review due to repeated cancellations. Contact admin to restore the ability to cancel bookings.
        </Alert>
      )}
      {!stats?.flagged_for_review && stats?.recent_cancels >= 2 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You have {stats.recent_cancels} cancellation{stats.recent_cancels === 1 ? '' : 's'} in the last 30 days. {stats.cancel_limit - stats.recent_cancels} more before your account is flagged for admin review.
        </Alert>
      )}

      <RegistrationProgress
        progress={stats?.registration_progress}
        onAction={() => navigate('/onboarding-test')}
      />

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <Grid item xs={12} sm={6} md={6} lg={3} key={card.label}>
              <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, minWidth: 0 }}>
                    {card.label}
                  </Typography>
                  <Icon sx={{ color: card.color, fontSize: 28, flexShrink: 0 }} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: card.color,
                    fontSize: 'clamp(1.05rem, 4vw, 2.125rem)',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    
                  }}
                  title={String(card.value)}
                >
                  {card.value}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
                  {card.sublabel}
                </Typography>
                <Button size="small" variant="outlined" onClick={card.action} fullWidth>
                  {card.actionLabel}
                </Button>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Your Referral Code</Typography>
            {stats?.referral_code ? (
              <Box>
                <Box sx={{
                  background: 'linear-gradient(135deg, #1e3a8a, #7c3aed)',
                  color: 'white',
                  p: 3,
                  borderRadius: 2,
                  textAlign: 'center',
                  mb: 2,
                }}>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>Your code</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 2 }}>
                    {stats.referral_code}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Earn $10 for every assessment booked with this code
                  </Typography>
                </Box>
                <Button variant="contained" fullWidth onClick={() => navigate('/referrals')}>
                  Share My Code
                </Button>
              </Box>
            ) : (
              <Alert severity="info">Your referral code will appear once your application is fully approved.</Alert>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Quick Actions</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button variant="outlined" startIcon={<WorkOutlineIcon />} onClick={() => navigate('/jobs')}>
                View Available Jobs
              </Button>
              <Button variant="outlined" startIcon={<EventIcon />} onClick={() => navigate('/availability')}>
                Update My Availability
              </Button>
              <Button variant="outlined" startIcon={<AttachMoneyIcon />} onClick={() => navigate('/earnings')}>
                View Earnings
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
